<template>
  <div class="batch-file-tab">
    <div class="cr-box" v-if="!tissEnabled">
      <div class="file-card">
        <div class="file-icon"><fa-icon :icon="['fal', 'print']" size="2x"/></div>
        <div class="file-main">
          <div class="file-name">Lote sem XML</div>
          <div class="file-meta">
            <span>Este plano não usa TISS</span>
            <span>o envio é feito com a capa impressa do lote</span>
          </div>
        </div>
        <button class="btn btn-primary btn-icon btn-icon-left" :disabled="busy || !canPrint"
                @click="$emit('cover')">
          <fa-icon :icon="['fal', 'print']"/>Capa do lote
        </button>
      </div>
    </div>

    <template v-else>
      <div class="cr-box" v-if="fileGenerated">
        <div class="file-card">
          <div class="file-icon"><fa-icon :icon="['fal', 'file-alt']" size="2x"/></div>
          <div class="file-main">
            <div class="file-name num">{{ batch.file.name || 'lote.xml' }}</div>
            <div class="file-meta">
              <span>ENVIO_LOTE_GUIAS</span>
              <span v-if="batch.file.size">{{ sizeLabel }}</span>
              <span>
                gerado em {{ batch.file.generatedAt | date('datetime') }}
                <template v-if="generatedBy">por {{ generatedBy }}</template>
              </span>
            </div>
            <div class="file-meta">
              <span>transação <b class="num">{{ transactionId }}</b></span>
              <span v-if="batch.file.hash">
                MD5 <b class="num">{{ shortHash }}</b>
                <button class="btn btn-sm btn-gray btn-action btn-icon file-copy tooltip"
                        :data-tooltip="copied ? 'Copiado' : 'Copiar o hash completo'"
                        @click="copyHash">
                  <fa-icon :icon="['fal', copied ? 'check' : 'copy']"/>
                </button>
              </span>
            </div>
          </div>
          <span class="chip chip-green file-chip" v-if="frozen">
            <fa-icon :icon="['fal', 'lock']"/>congelado no envio
          </span>
        </div>

        <div class="batch-file-actions">
          <button class="btn btn-primary btn-icon btn-icon-left"
                  :class="{loading: busyAction === 'xml'}"
                  :disabled="busy || !canXml"
                  @click="$emit('download')">
            <fa-icon :icon="['fal', 'cloud-download']"/>{{ closed ? 'Gerar XML' : 'Baixar XML' }}
          </button>
          <button class="btn btn-gray btn-icon btn-icon-left" v-if="closed"
                  :class="{loading: busyAction === 'validate'}" :disabled="busy"
                  @click="$emit('validate')">
            <fa-icon :icon="['fal', 'check-double']"/>Validar
          </button>
          <button class="btn btn-gray btn-icon btn-icon-left" :disabled="busy || !canPrint"
                  @click="$emit('cover')">
            <fa-icon :icon="['fal', 'print']"/>Capa do lote
          </button>
        </div>
        <div class="batch-help">{{ helpText }}</div>
      </div>

      <div class="cr-box" v-else>
        <div class="empty-inline">
          <div class="empty-icon"><fa-icon :icon="['fal', 'file-alt']" size="2x"/></div>
          <div class="empty-title">XML ainda não gerado</div>
          <div v-if="canceled">Lote cancelado não gera XML.</div>
          <div v-else>
            Gere o XML TISS para enviar o lote pelo portal ou webservice da operadora.
          </div>
          <div class="batch-file-actions is-center" v-if="!canceled">
            <button class="btn btn-primary btn-icon btn-icon-left"
                    :class="{loading: busyAction === 'xml'}"
                    :disabled="busy || !canXml"
                    @click="$emit('download')">
              <fa-icon :icon="['fal', 'cloud-download']"/>Gerar XML TISS
            </button>
            <button class="btn btn-gray btn-icon btn-icon-left" :disabled="busy || !canPrint"
                    @click="$emit('cover')">
              <fa-icon :icon="['fal', 'print']"/>Capa do lote
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  props: {
    batch: {
      type: Object,
      required: true,
    },
    busyAction: {
      type: String,
      default: null,
    },
    canXml: {
      type: Boolean,
      default: true,
    },
    canPrint: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      copied: false,
    };
  },
  computed: {
    tissEnabled() {
      return !!(this.batch.tiss && this.batch.tiss.enabled);
    },
    fileGenerated() {
      return !!(this.batch.file && this.batch.file.generatedAt);
    },
    closed() {
      return this.batch.status === 'closed';
    },
    canceled() {
      return this.batch.status === 'canceled';
    },
    frozen() {
      return this.fileGenerated && !this.closed;
    },
    busy() {
      return !!this.busyAction;
    },
    transactionId() {
      return this.batch.file.transactionId || this.batch.number;
    },
    shortHash() {
      const hash = (this.batch.file && this.batch.file.hash) || '';
      if (!hash) return '—';
      return hash.length > 16 ? `${hash.slice(0, 12)}…${hash.slice(-4)}` : hash;
    },
    sizeLabel() {
      const size = this.batch.file.size || 0;
      return size > 1024 ? `${(size / 1024).toFixed(1).replace('.', ',')} KB` : `${size} bytes`;
    },
    generatedBy() {
      const events = this.batch.events || [];
      const event = [...events].reverse().find(({ type }) => type === 'xml_generated');
      return event && event.by && event.by.name ? event.by.name : null;
    },
    helpText() {
      if (this.frozen) {
        return 'Arquivo congelado no envio: o download entrega exatamente o XML '
          + 'que foi enviado à operadora.';
      }
      return 'Enquanto o lote está fechado, baixar gera o XML de novo com os dados atuais '
        + 'das guias. Ele congela quando você marca o lote como enviado.';
    },
  },
  methods: {
    copyHash() {
      const { hash } = this.batch.file;
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(hash).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      }).catch(() => {});
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.batch-file-tab {
  .file-card {
    align-items: flex-start;
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    display: flex;
    gap: $layout-spacing-lg;
    margin-top: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;
    .file-icon {
      color: $gray-color-dark;
      flex: 0 0 auto;
      line-height: 1;
      padding-top: .1rem;
    }
    .file-main {
      flex: 1 1 auto;
      min-width: 0;
    }
    .file-name {
      font-weight: 600;
      overflow-wrap: anywhere;
    }
    .file-meta {
      align-items: center;
      color: $gray-color-dark;
      display: flex;
      flex-wrap: wrap;
      font-size: $font-size-sm;
      gap: .3rem;
      margin-top: .1rem;
      b {
        color: $body-font-color;
        font-weight: 600;
      }
      span + span::before {
        color: $border-color-dark;
        content: '·';
        margin-right: .3rem;
      }
      .file-copy {
        margin-left: .2rem;
      }
    }
    .file-chip {
      flex: 0 0 auto;
    }
  }
  .batch-file-actions {
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    margin-top: $layout-spacing;
    &.is-center {
      justify-content: center;
    }
  }
  .batch-help {
    margin-top: $layout-spacing-sm;
  }
  @media screen and (max-width: $size-sm) {
    .file-card {
      flex-wrap: wrap;
      .file-main {
        flex: 1 1 100%;
      }
    }
  }
}
</style>
